export type SearchTerm = Readonly<{ value: string; phrase: boolean; prefix: boolean }>;
export type SearchPlan = readonly (readonly SearchTerm[])[];
const words = (text: string): string[] => text.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];

/** Quoted phrases, implicit AND, explicit OR, and trailing prefix wildcards. */
export function parseSearchQuery(query: string): SearchPlan {
  const groups: SearchTerm[][] = [[]];
  for (const token of query.slice(0, 256).match(/"[^"]+"|[^\s]+/g) ?? []) {
    if (token === "OR") { if (groups[groups.length - 1]?.length && groups.length < 8) groups.push([]); continue; }
    const phrase = token.startsWith('"') && token.endsWith('"');
    const value = words(token).join(" ");
    if (!value) continue;
    const group = groups[groups.length - 1];
    if (group && group.length < 8) group.push({ value, phrase, prefix: !phrase && token.endsWith("*") });
  }
  return groups.filter(group => group.length > 0);
}

export function matchesSearchPlan(text: string, plan: SearchPlan): boolean {
  const tokens = words(text);
  const normalized = ` ${tokens.join(" ")} `;
  return plan.some(group => group.every(term => term.phrase
    ? normalized.includes(` ${term.value} `)
    : term.prefix ? tokens.some(token => token.startsWith(term.value))
      : term.value.split(" ").every(word => tokens.includes(word))));
}

export const searchIndexQueries = (plan: SearchPlan): string[] =>
  plan.map(group => {
    // Convex prefix-matches only the last indexed term. Other wildcard terms
    // must be left to the post-filter, not accidentally required as exact words.
    const prefix = group.find(term => term.prefix)?.value.split(" ").slice(-1)[0];
    const exact = group.filter(term => !term.prefix).flatMap(term => term.value.split(" "));
    return [...exact.slice(0, prefix ? 15 : 16), ...(prefix ? [prefix] : [])].join(" ");
  });

export function searchSegments(text: string, query: string): readonly { text: string; highlighted: boolean }[] {
  const terms = parseSearchQuery(query).flat();
  const values = [...new Set(terms.flatMap(term => term.value.split(" ")))].sort((a,b) => b.length-a.length);
  if (!values.length) return [{ text, highlighted: false }];
  // Values contain only letters/digits, so they cannot introduce regex syntax.
  const pattern = new RegExp(`(?<![\\p{L}\\p{N}])(?:${values.join("|")})[\\p{L}\\p{N}]*`, "giu");
  const parts: { text: string; highlighted: boolean }[] = [];
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    if (match.index > cursor) parts.push({ text: text.slice(cursor, match.index), highlighted: false });
    const token = match[0].toLocaleLowerCase();
    const highlighted = terms.some(term => term.value.split(" ").some(word => term.prefix ? token.startsWith(word) : token === word));
    parts.push({ text: match[0], highlighted }); cursor = match.index + match[0].length;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), highlighted: false });
  return parts;
}
